import * as echarts from "echarts";
import { useLayoutEffect, useRef } from "react";

import { IAnalyzeState } from "../types";

interface IProps {
    pieData: IAnalyzeState[] | undefined;
    category: string;
    isZoomed?: boolean;
}

const usePieChartCircleZoom = ({ category, pieData, isZoomed = false }: IProps) => {
    const MPieChart = useRef(null);

    useLayoutEffect(() => {
        if (!pieData) return;

        // 等待 DOM 元素準備好
        const initChart = () => {
            if (!MPieChart.current) {
                setTimeout(initChart, 10);
                return;
            }

            let pieChart: echarts.ECharts | null = null;

            try {
                pieChart = echarts.init(MPieChart.current);
            } catch (error) {
                return;
            }

            if (!pieChart) return;

            // 計算總數以用於百分比計算
            const totalValue = pieData?.reduce((sum, item) => sum + item.value, 0) || 0;

            const option = {
                title: {
                    text: category,
                    left: "center",
                    top: "5%",
                    textStyle: {
                        fontSize: isZoomed ? 24 : 16,
                        fontWeight: "bold",
                    },
                },
                grid: {
                    top: "15%",
                    left: "5%",
                    right: "5%",
                    bottom: "10%",
                    containLabel: true,
                },
                color: ["#7D2148", "#41768C", "#F46424", "#87919A", "#B08BCD", "#769D85"],
                label: {
                    fontSize: isZoomed ? 18 : 16,
                    fontWeight: isZoomed ? "bold" : "normal",
                },
                tooltip: {
                    trigger: "item",
                    formatter: (params: { seriesName: string; name: string; value: number }) => {
                        const percentage = totalValue > 0 ? ((params.value / totalValue) * 100).toFixed(1) : "0.0";
                        return `${params.name}: ${percentage}%<br/>數量: ${params.value}`;
                    },
                    textStyle: {
                        fontSize: isZoomed ? 16 : 14,
                    },
                },
                legend: {
                    orient: "vertical",
                    right: isZoomed ? "2%" : "5%",
                    top: "middle",
                    width: isZoomed ? "25%" : "auto",
                    textStyle: {
                        fontSize: isZoomed ? 14 : 12,
                    },
                },
                itemStyle: {
                    borderRadius: isZoomed ? 8 : 4,
                    borderWidth: isZoomed ? 2 : 1,
                    borderColor: "#fff",
                },
                series: [
                    {
                        name: category,
                        startAngle: 90,
                        type: "pie",
                        radius: isZoomed ? ["20%", "45%"] : ["30%", "65%"],
                        center: isZoomed ? ["30%", "50%"] : ["40%", "50%"],
                        data: pieData,
                        emphasis: {
                            itemStyle: {
                                shadowBlur: isZoomed ? 30 : 20,
                                shadowOffsetX: 0,
                                borderRadius: isZoomed ? 10 : 5,
                                shadowColor: "rgba(0, 0, 0, 0.5)",
                            },
                        },
                        labelLine: {
                            length: isZoomed ? 20 : 15,
                            length2: isZoomed ? 30 : 20,
                        },
                    },
                ],
            };

            try {
                pieChart.setOption(option);
            } catch (error) {
                return;
            }

            // 響應式調整
            const handleResize = () => {
                if (pieChart && !pieChart.isDisposed()) {
                    try {
                        pieChart.resize();
                    } catch (error) {
                        // 靜默處理錯誤
                    }
                }
            };

            window.addEventListener("resize", handleResize);

            return () => {
                window.removeEventListener("resize", handleResize);
                if (pieChart && !pieChart.isDisposed()) {
                    try {
                        pieChart.dispose();
                    } catch (error) {
                        // 靜默處理錯誤
                    }
                }
            };
        };

        initChart();
    }, [category, pieData, isZoomed]);

    return MPieChart;
};

export default usePieChartCircleZoom;
